import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, User, Mail, Phone, Loader2, Cake } from "lucide-react";
import { toast } from "sonner";
import { api, imageUrl, money, formatApiError, fullDate } from "@/lib/api";
import { usePublicSettings } from "@/lib/settings";
import { Checkbox } from "@/components/ui/checkbox";

export default function Checkout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = usePublicSettings();
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState({});
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [cust, setCust] = useState({
    first_name: "", last_name: "", email: "", phone: "", birthday: "",
    marketing_email_consent: true, terms_accepted: false,
  });

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setEvent(r.data)).catch(() => navigate("/events"));
  }, [slug, navigate]);

  if (!event) return <div className="py-20 text-center text-zinc-500">Loading…</div>;

  const tickets = (event.tickets || []).filter((t) => t.active).sort((a, b) => a.display_order - b.display_order);
  const setQ = (id, v, t) => {
    const max = Math.min(t.max_purchase, t.remaining == null ? 99 : t.remaining);
    const nv = Math.max(0, Math.min(v, max));
    setQty((q) => ({ ...q, [id]: nv }));
  };

  const items = tickets.map((t) => ({ t, q: qty[t.id] || 0 })).filter((x) => x.q > 0);
  const subtotal = items.reduce((s, x) => s + x.t.price * x.q, 0);
  const feePct = settings?.service_fee_percent || 0;
  const feeFlat = settings?.service_fee_flat || 0;
  const taxPct = settings?.tax_percent || 0;
  const fees = subtotal > 0 ? subtotal * (feePct / 100) + feeFlat : 0;
  const tax = subtotal * (taxPct / 100);
  const total = subtotal + fees + tax;
  const count = items.reduce((s, x) => s + x.q, 0);

  const goToInfo = () => {
    if (count === 0) { toast.error("Please select at least one ticket."); return; }
    setStep(2);
    window.scrollTo(0, 0);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!cust.terms_accepted) { toast.error("Please accept the terms to continue."); return; }
    setSubmitting(true);
    try {
      const payload = {
        event_id: event.id,
        items: items.map((x) => ({ ticket_type_id: x.t.id, quantity: x.q })),
        customer: { ...cust, marketing_sms_consent: cust.marketing_email_consent && !!cust.phone },
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/create", payload);
      if (data.free) {
        navigate(`/orders/${data.order_number}`);
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const field = "w-full rounded-md bg-[#1A1A1F] border border-[#27272A] pl-10 pr-4 py-3.5 text-white placeholder-zinc-500 outline-none focus:border-[#D4AF37]";

  return (
    <div className="pb-40 md:pb-16">
      <div className="mx-auto max-w-xl px-4 sm:px-6 pt-4">
        <button onClick={() => (step === 2 ? setStep(1) : navigate(`/events/${slug}`))} data-testid="checkout-back" className="inline-flex items-center gap-1.5 font-oswald text-xs font-semibold tracking-widest text-zinc-300 hover:text-white">
          <ArrowLeft size={15} /> {step === 2 ? "BACK" : "BACK TO EVENT"}
        </button>
      </div>

      <div className="mx-auto max-w-xl px-4 sm:px-6 mt-4">
        <div className="flex items-center gap-3 mb-6">
          <img src={imageUrl(event.flyer_image)} alt="" className="h-14 w-14 rounded-lg object-cover" />
          <div>
            <h1 className="font-display text-xl uppercase text-white leading-none">{event.name}</h1>
            <p className="text-xs text-zinc-400 mt-1">{fullDate(event.date)}</p>
          </div>
        </div>

        {step === 1 && (
          <>
            <h2 className="font-display text-3xl uppercase text-white mb-4">Select Tickets</h2>
            <div className="space-y-3" data-testid="ticket-list">
              {tickets.map((t) => {
                const q = qty[t.id] || 0;
                const soldOut = t.sold_out || t.remaining === 0;
                return (
                  <div key={t.id} data-testid={`ticket-${t.id}`} className="rounded-xl border border-[#27272A] bg-[#141417] p-4 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-oswald text-base font-semibold text-white uppercase tracking-wide">{t.name}</p>
                      {t.description && <p className="text-xs text-zinc-400 mt-0.5">{t.description}</p>}
                      <p className="text-[#D4AF37] font-bold mt-1">{t.price > 0 ? money(t.price) : "FREE"}</p>
                      {t.remaining != null && t.remaining <= 10 && t.remaining > 0 && <p className="text-[10px] text-amber-400 mt-0.5">Only {t.remaining} left</p>}
                    </div>
                    {soldOut ? (
                      <span className="font-oswald text-xs tracking-widest text-zinc-500 shrink-0">SOLD OUT</span>
                    ) : (
                      <div className="flex items-center gap-3 shrink-0">
                        <button data-testid={`ticket-minus-${t.id}`} onClick={() => setQ(t.id, q - 1, t)} className="h-8 w-8 rounded-md border border-[#27272A] text-white flex items-center justify-center disabled:opacity-30" disabled={q === 0}><Minus size={16} /></button>
                        <span data-testid={`ticket-qty-${t.id}`} className="w-5 text-center text-white font-semibold">{q}</span>
                        <button data-testid={`ticket-plus-${t.id}`} onClick={() => setQ(t.id, q + 1, t)} className="h-8 w-8 rounded-md border border-[#27272A] text-white flex items-center justify-center"><Plus size={16} /></button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {step === 2 && (
          <form onSubmit={placeOrder}>
            <h2 className="font-display text-3xl uppercase text-white mb-1">Attendee Information</h2>
            <p className="text-sm text-zinc-400 mb-5">We'll use this to send your tickets and keep you updated on future events.</p>
            <div className="space-y-3">
              <div className="relative"><User size={16} className="absolute left-3.5 top-4 text-zinc-500" /><input data-testid="cust-first-name" required placeholder="First Name *" className={field} value={cust.first_name} onChange={(e) => setCust({ ...cust, first_name: e.target.value })} /></div>
              <div className="relative"><User size={16} className="absolute left-3.5 top-4 text-zinc-500" /><input data-testid="cust-last-name" required placeholder="Last Name *" className={field} value={cust.last_name} onChange={(e) => setCust({ ...cust, last_name: e.target.value })} /></div>
              <div className="relative"><Mail size={16} className="absolute left-3.5 top-4 text-zinc-500" /><input data-testid="cust-email" required type="email" placeholder="Email Address *" className={field} value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} /></div>
              <div className="relative"><Phone size={16} className="absolute left-3.5 top-4 text-zinc-500" /><input data-testid="cust-phone" required placeholder="Phone Number *" className={field} value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} /></div>
              <div className="rounded-md bg-[#1A1A1F] border border-[#27272A] px-4 py-3">
                <label className="flex items-center gap-2 text-sm text-zinc-200"><Cake size={16} className="text-[#D4AF37]" /> Your Birthday <span className="text-zinc-500 text-xs">(optional)</span></label>
                <p className="text-[11px] text-zinc-500 mt-0.5 mb-2">Add it and we'll hook you up with exclusive birthday-night offers.</p>
                <input data-testid="cust-birthday" type="date" className="w-full rounded-md bg-[#141417] border border-[#27272A] px-3 py-2.5 text-white outline-none focus:border-[#D4AF37]" value={cust.birthday} onChange={(e) => setCust({ ...cust, birthday: e.target.value })} />
              </div>
            </div>

            <label className="flex items-start gap-3 mt-5 cursor-pointer">
              <Checkbox data-testid="consent-marketing" checked={cust.marketing_email_consent} onCheckedChange={(v) => setCust({ ...cust, marketing_email_consent: !!v })} className="mt-0.5 border-[#D4AF37] data-[state=checked]:bg-[#D4AF37]" />
              <span className="text-sm text-zinc-300">Send me updates about upcoming events, exclusive tickets and special offers.</span>
            </label>
            <label className="flex items-start gap-3 mt-3 cursor-pointer">
              <Checkbox data-testid="consent-terms" checked={cust.terms_accepted} onCheckedChange={(v) => setCust({ ...cust, terms_accepted: !!v })} className="mt-0.5 border-zinc-500 data-[state=checked]:bg-[#D4AF37]" />
              <span className="text-sm text-zinc-300">I agree to the <Link to="/legal/terms" className="text-[#D4AF37] underline">Terms</Link> & <Link to="/legal/privacy" className="text-[#D4AF37] underline">Privacy Policy</Link>.</span>
            </label>

            <div className="mt-6 rounded-xl border border-[#27272A] bg-[#141417] p-4 space-y-1.5 text-sm">
              {items.map((x) => (
                <div key={x.t.id} className="flex justify-between text-zinc-300"><span>{x.t.name} × {x.q}</span><span>{money(x.t.price * x.q)}</span></div>
              ))}
              <div className="border-t border-[#27272A] my-2" />
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>Service Fees</span><span>{money(fees)}</span></div>
              {tax > 0 && <div className="flex justify-between text-zinc-400"><span>Tax</span><span>{money(tax)}</span></div>}
              <div className="flex justify-between text-white font-bold text-base pt-1"><span>Total</span><span className="text-[#D4AF37]">{money(total)}</span></div>
            </div>

            <button data-testid="place-order-btn" disabled={submitting} className="btn-gold w-full mt-5 rounded-lg py-4 font-oswald tracking-widest disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <><Loader2 size={18} className="animate-spin" /> PROCESSING…</> : total > 0 ? "CONTINUE TO PAYMENT" : "COMPLETE FREE RSVP"}
            </button>
          </form>
        )}
      </div>

      {step === 1 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#121215]/95 backdrop-blur border-t border-[#27272A] p-4">
          <div className="mx-auto max-w-xl flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-zinc-400">{count} ticket{count !== 1 ? "s" : ""}</p>
              <p className="font-display text-2xl text-white leading-none">{money(total)}</p>
            </div>
            <button data-testid="continue-btn" onClick={goToInfo} className="btn-gold flex-1 max-w-[60%] rounded-lg py-4 font-oswald tracking-widest">CONTINUE</button>
          </div>
        </div>
      )}
    </div>
  );
}
